import classNames from "classnames";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { generatePath, useHistory, useRouteMatch } from "react-router-dom";
import PrivateLayout from "layouts/private/private";
import LoadingIndicator from "components/partial/loading-indicator/loading-indicator";
import StarRating from "components/partial/star-rating/star-rating";
import Button from "components/base/button/button";
import { ReactComponent as ClockIcon } from "assets/icons/ic_clock.svg";
import { ReactComponent as BookIcon } from "assets/icons/ic_book.svg";
import { ReactComponent as MenuIcon } from "assets/icons/ic_burger_menu.svg";
import { ReactComponent as HeartIcon } from "assets/icons/ic_heart.svg";
import { ReactComponent as EyeIcon } from "assets/icons/ic_eye-on.svg";
import { ReactComponent as ShareIcon } from "assets/icons/ic_share.svg";
import { ReactComponent as CheckIcon } from "assets/icons/ic_check.svg";
import { copyTextToClipboard } from "helpers/clipboard.helper";
import { getPicture } from "helpers/image.helper";
import { formatCourseDuration } from "helpers/time.helper";
import { IRatingPayloadModel } from "models/dto/ZoomiLxp/Models/EventModels/IRatingPayloadModel";
import { IGroupHomePageModel } from "models/dto/ZoomiLxp/Models/GroupModels/IGroupHomePageModel";
import { ICertificationRequirementModel } from "models/dto/ZoomiLxp/Models/GroupModels/ICertificationRequirementModel";
import { CertificationRequirementType } from "models/dto/ZoomiLxp/Utilities/Enumerations/CertificationRequirementType";
import { EventTypes } from "models/dto/ZoomiLxp/Utilities/Enumerations/EventTypes";
import { Routes } from "routes";
import { useAppStore } from "store";
import styles from "./styles.module.scss";

const DESCRIPTION_MAX_LENGTH = 400;
const MAX_VIEWS: number = 999;

const CERTIFICATION_REQUIREMENT_LABELS: Record<CertificationRequirementType, string> = {
	[CertificationRequirementType.Progress]: "Courses Completed",
	[CertificationRequirementType.Z1]: "Average Z1",
	[CertificationRequirementType.Exam]: "Courses with Final Assessments Passed (Score of 80% or higher required)",
	[CertificationRequirementType.Engagement]: "Courses with Minimum Engagement Met (Score of 65 or higher required)",
};

const formatPrice = (price: number): string => {
	const dollars = price / 100;
	return price % 100 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`;
};

const formatCount = (value?: number): string => (value ?? 0).toLocaleString("en-US").replaceAll(",", " ");

const formatCompactCount = (value?: number): string => {
	const count = value ?? 0;
	return count > MAX_VIEWS ? `${Number(count / 1000).toFixed(0)}K` : count.toString();
};

const formatCertificationValue = (requirement: ICertificationRequirementModel): string => {
	return formatCertificationRequirementValue(requirement, requirement.currentValue);
};

const formatCertificationThreshold = (requirement: ICertificationRequirementModel): string => {
	return formatCertificationRequirementValue(requirement, requirement.thresholdValue);
};

const formatCertificationRequirementValue = (requirement: ICertificationRequirementModel, value: number): string => {
	const formattedValue = Number.isInteger(value) ? value.toString() : value.toFixed(1);

	return requirement.type === CertificationRequirementType.Z1 ? formattedValue : `${formattedValue}%`;
};

const getShortDescription = (description: string, isExpanded: boolean): string => {
	if (isExpanded || description.length <= DESCRIPTION_MAX_LENGTH) return description;

	return `${description.slice(0, DESCRIPTION_MAX_LENGTH).trim()}...`;
};

const getActionButtonLabel = (isGroupMember: boolean, isPaidGroup: boolean, isJoining: boolean): string => {
	if (isGroupMember) return "Joined";
	if (isPaidGroup) return "Buy";
	if (isJoining) return "Joining...";

	return "Join";
};

const GroupPage = () => {
	const match = useRouteMatch<{ id: string }>();
	const history = useHistory();
	const groupId = Number(match.params?.id ?? 0);
	const { billingStore, groupStore, toasterStore } = useAppStore();
	const [isExpandedDescription, setIsExpandedDescription] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [isJoining, setIsJoining] = useState(false);
	const [rating, setRating] = useState(0);
	const [userRating, setUserRating] = useState(0);

	useEffect(() => {
		if (groupId > 0) {
			setIsLoading(true);
			setErrorMessage("");
			setIsExpandedDescription(false);
			groupStore
				.getGroupHomeData(groupId)
				.catch((err) => {
					console.log(err);
					setErrorMessage("Group not found");
				})
				.finally(() => setIsLoading(false));
		}
	}, [groupId, groupStore]);

	const group: IGroupHomePageModel | undefined = groupStore.groupHomeData;
	const isGroupMember = Boolean(group?.isMember);
	const courses = group?.courses ?? [];
	const description = group?.description ?? "";
	const isDescriptionExpandable = description.length > DESCRIPTION_MAX_LENGTH;
	const shownDescription = getShortDescription(description, isExpandedDescription);
	const price = group?.price ?? 0;
	const isPaidGroup = Boolean(price);
	const actionButtonLabel = getActionButtonLabel(isGroupMember, isPaidGroup, isJoining);
	const isActionButtonDisabled = isGroupMember || isJoining;
	const ratingLabel = !isGroupMember
		? "Join group to rate"
		: userRating > 0
		? `Your rating is ${userRating} Star${userRating > 1 ? "s" : ""}`
		: "Rate this group";
	const progressPercent = Math.min(Math.max(group?.progressPercent ?? 0, 0), 100);
	const courseCount = group?.courseCount ?? courses.length;
	const contentCount = group?.contentCount ?? 0;
	const memberCount = group?.memberCount ?? 0;
	const durationMinutes = group?.durationMinutes ?? 0;
	const certificationSummary = group?.certificationSummary;
	const certificationProgressPercent = Math.min(Math.max(certificationSummary?.overallProgressPercent ?? 0, 0), 100);
	const certificationRequirements = certificationSummary?.requirements ?? [];
	const showCertificationSummary = Boolean(
		group?.isCertificationGroup && isGroupMember && certificationRequirements.length > 0
	);

	useEffect(() => {
		setRating(group?.rating ?? 0);
		setUserRating(group?.userRating ?? 0);
	}, [group?.id, group?.rating, group?.userRating]);

	const onShare = () => {
		copyTextToClipboard(window.location.href).then(() => {
			toasterStore.showSuccessMessage("Link copied to clipboard");
		});
	};

	const handleRate = async (newRating: number) => {
		if (!group?.id || !isGroupMember) return;

		const payload: IRatingPayloadModel = { rating: newRating };
		setRating(newRating);
		setUserRating(newRating);
		await groupStore.sendGroupEvent({
			groupId: group.id,
			eventType: EventTypes.GroupRated,
			payload: JSON.stringify(payload),
		});
	};

	const handleLike = async () => {
		if (!group?.id || !isGroupMember) return;

		const previousGroup = group;
		const nextIsLiked = !group.isLiked;
		const nextLikesCount = Math.max((group.likesCount ?? 0) + (nextIsLiked ? 1 : -1), 0);

		groupStore.groupHomeData = {
			...group,
			isLiked: nextIsLiked,
			likesCount: nextLikesCount,
		};

		try {
			await groupStore.sendGroupEvent({
				groupId: group.id,
				eventType: nextIsLiked ? EventTypes.GroupLiked : EventTypes.GroupUnliked,
				payload: null as unknown as string,
			});
		} catch (err) {
			console.log(err);
			groupStore.groupHomeData = previousGroup;
		}
	};

	const handleJoinGroup = async () => {
		if (!group?.id || isGroupMember || isPaidGroup || isJoining) return;

		setIsLoading(true);
		setIsJoining(true);
		try {
			await groupStore.joinGroup(group.id);
		} catch (err) {
			toasterStore.showErrorMessage(err);
		} finally {
			setIsLoading(false);
			setIsJoining(false);
		}
	};

	const handleBuyGroup = () => {
		if (!group?.id || isGroupMember || !isPaidGroup) return;

		billingStore.createGroupCheckoutSession(group.id).then((checkoutUrl: string) => window.open(checkoutUrl, "_blank"));
	};

	const handleActionButtonClick = () => {
		if (isPaidGroup) {
			handleBuyGroup();
			return;
		}

		handleJoinGroup();
	};

	return (
		<PrivateLayout className={styles.group_page__container}>
			<LoadingIndicator loading={isLoading} spinnerPosition={"center"} backgroundStyle={"white"}>
				<div className={styles.group_page}>
					{errorMessage ? (
						<div className={styles.group_page__nodata}>{errorMessage}</div>
					) : (
						<div className={styles.group_page__box}>
							<div className={styles.group_page__card}>
								<div className={styles.group_page__color_bar}>
									{progressPercent ? (
										<div
											className={styles.group_page__color_bar_incompleted}
											style={{ width: `${100 - progressPercent}%` }}
										/>
									) : (
										<></>
									)}
								</div>
								<div className={styles.group_page__meta}>
									<div className={styles.group_page__meta_item}>
										<ClockIcon className={styles.group_page__meta_icon} />
										<span>{formatCourseDuration(durationMinutes) || "0 min"}</span>
									</div>
									<div className={styles.group_page__meta_item}>
										<BookIcon className={styles.group_page__meta_icon} />
										<span>{courseCount} courses</span>
									</div>
									<div className={styles.group_page__meta_item}>
										<MenuIcon className={styles.group_page__meta_icon} />
										<span>{contentCount} contents</span>
									</div>
								</div>
								<div className={styles.group_page__main}>
									<div className={styles.group_page__content}>
										<img
											src={group ? getPicture(group) : ""}
											alt="Group thumbnail"
											className={styles.group_page__thumbnail}
										/>
										<div className={styles.group_page__description_block}>
											<h1 className={styles.group_page__title}>{group?.displayName}</h1>
											{description && (
												<div className={styles.group_page__description}>
													{shownDescription}
													{isDescriptionExpandable && (
														<button
															type="button"
															className={styles.group_page__more}
															onClick={() => setIsExpandedDescription((prev) => !prev)}
														>
															{isExpandedDescription ? "Less" : "More"}
														</button>
													)}
												</div>
											)}
										</div>
									</div>
									<div className={styles.group_page__side}>
										<div className={styles.group_page__rating_row}>
											<div className={styles.group_page__rating}>{(group?.rating ?? 0).toFixed(1)}</div>
											<div className={styles.group_page__views}>{formatCompactCount(group?.ratingCount)}</div>
										</div>
										<StarRating
											className={styles.group_page__stars}
											value={rating}
											isDisabled={!isGroupMember}
											onRatingChange={async (newRating) => {
												await handleRate(newRating);
											}}
											color="var(--primary)"
										/>
										<div className={styles.group_page__rating_label}>{ratingLabel}</div>
										{isPaidGroup && !isGroupMember && (
											<div className={styles.group_page__price}>{formatPrice(price)}</div>
										)}
										<Button
											label={actionButtonLabel}
											onClick={handleActionButtonClick}
											className={styles.group_page__buy_button}
											disabled={isActionButtonDisabled}
										/>
									</div>
								</div>
								<div className={styles.group_page__footer}>
									<button
										type="button"
										className={classNames(styles.group_page__footer_button, {
											[styles.group_page__footer_button_disabled]: !isGroupMember,
										})}
										onClick={handleLike}
										disabled={!isGroupMember}
										title={isGroupMember ? undefined : "Join group to like"}
									>
										<HeartIcon
											className={classNames(styles.group_page__footer_icon, styles.group_page__heart_icon, {
												[styles.group_page__heart_icon_active]: group?.isLiked,
											})}
										/>
										<span>{formatCount(group?.likesCount)}</span>
									</button>
									<div className={styles.group_page__footer_item}>
										<EyeIcon className={classNames(styles.group_page__footer_icon, styles.group_page__eye_icon)} />
										<span>{formatCount(group?.viewCount)}</span>
									</div>
									<div className={styles.group_page__footer_item}>
										<span className={styles.group_page__members_icon} />
										<span>{formatCount(memberCount)}</span>
									</div>
									<button type="button" className={styles.group_page__share_button} onClick={onShare}>
										<ShareIcon className={styles.group_page__footer_icon} />
										<span>Share</span>
									</button>
								</div>
							</div>
							{showCertificationSummary && (
								<section className={styles.group_page__certification}>
									<div className={styles.group_page__certification_progress}>
										<div className={styles.group_page__certification_progress_label}>Progress</div>
										<div className={styles.group_page__certification_progress_bar}>
											<div
												className={styles.group_page__certification_progress_incompleted}
												style={{ width: `${100 - certificationProgressPercent}%` }}
											/>
										</div>
									</div>
									<div className={styles.group_page__certification_card}>
										<div className={styles.group_page__certification_status}>
											<span className={styles.group_page__certification_status_label}>Certification Status -</span>
											<span
												className={classNames({
													[styles.group_page__certification_status_complete]: certificationSummary?.isComplete,
													[styles.group_page__certification_status_incomplete]: !certificationSummary?.isComplete,
												})}
											>
												{certificationSummary?.isComplete ? "Complete" : "Incomplete"}
											</span>
											{certificationSummary?.isComplete && (
												<CheckIcon className={styles.group_page__certification_status_icon} />
											)}
										</div>
										<div className={styles.group_page__certification_table}>
											<div className={styles.group_page__certification_header}>
												<div />
												<div>Current Progress</div>
												<div>Requirement</div>
												<div>Requirement Satisfied</div>
											</div>
											{certificationRequirements.map((requirement) => (
												<div
													className={styles.group_page__certification_row}
													key={`${requirement.type}-${requirement.mode}`}
												>
													<div className={styles.group_page__certification_requirement}>
														{CERTIFICATION_REQUIREMENT_LABELS[requirement.type]}
													</div>
													<div>{formatCertificationValue(requirement)}</div>
													<div>{formatCertificationThreshold(requirement)}</div>
													<div className={styles.group_page__certification_result}>
														{requirement.isSatisfied ? (
															<CheckIcon className={styles.group_page__certification_check} />
														) : (
															<span className={styles.group_page__certification_cross}>X</span>
														)}
													</div>
												</div>
											))}
										</div>
									</div>
								</section>
							)}
							{courses.length > 0 && (
								<section className={styles.group_page__courses}>
									<h2 className={styles.group_page__courses_title}>Courses</h2>
									<div className={styles.group_page__courses_list}>
										{courses.map((course) => (
											<div
												className={styles.group_page__course_row}
												key={course.id}
												onClick={() => history.push(generatePath(Routes.CoursePage, { id: course.id }))}
											>
												<img
													src={getPicture(course)}
													alt={course.title}
													className={styles.group_page__course_thumbnail}
												/>
												<div className={styles.group_page__course_title}>{course.title}</div>
												<div className={styles.group_page__course_meta}>
													<span>{course.authorName}</span>
													<span>{formatCourseDuration(course.durationMinutes)}</span>
													<span>Z1 - {Math.round(course.z1)}%</span>
													<span>Z2 - {Math.round(course.z2)}%</span>
													<CheckIcon
														className={classNames(styles.group_page__course_check, {
															[styles.group_page__course_check_completed]: course.isCompleted,
														})}
													/>
												</div>
											</div>
										))}
									</div>
								</section>
							)}
						</div>
					)}
				</div>
			</LoadingIndicator>
		</PrivateLayout>
	);
};

export default observer(GroupPage);
